import { memo } from "react";

import CancelIcon from "@mui/icons-material/CloseRounded";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import PropTypes from "prop-types";

import { useCancelOrDestroyShippingParameterVariantMutation } from "@features/purchaseOrders/data/shippingParameterQueries";
import { LoadingButton } from "@features/ui";

import { useInput } from "../../hooks/inputs/useInput";

const useStyles = makeStyles((theme) => ({
  ...theme.global,
  buttonRow: {
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    width: "100%",
  },
}));

const EditShippingParameterVariantModal = ({
  open,
  shippingParameterVariantId,
  purchaseOrderId,
  handleClose,
}) => {
  const classes = useStyles();

  const { mutate: cancelOrDestroyShippingParameterVariant, isPending } =
    useCancelOrDestroyShippingParameterVariantMutation();

  const { value, bind } = useInput("");

  const handleCancel = () => {
    cancelOrDestroyShippingParameterVariant(
      {
        id: shippingParameterVariantId,
        purchaseOrderId,
        action: "cancel",
        cancelationNote: value,
      },
      { onSuccess: handleClose }
    );
  };

  const handleDestroy = () => {
    cancelOrDestroyShippingParameterVariant(
      {
        id: shippingParameterVariantId,
        purchaseOrderId,
        action: "destroy",
      },
      { onSuccess: handleClose }
    );
  };

  return (
    <div className={classes.relativeContainer}>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        disableScrollLock
      >
        <DialogTitle>
          <Typography className={classes.headerText}>
            {`Cancel or Destroy Shipping Parameter Item #${shippingParameterVariantId}`}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <IconButton
            className={classes.closeButton}
            onClick={handleClose}
            size="large"
          >
            <CancelIcon fontSize="large" color="secondary" />
          </IconButton>
          <br />
          <div className={classes.fullWidthCenterColumn}>
            <TextField
              size="small"
              className={classes.settingsMargin}
              variant="outlined"
              color="secondary"
              name="cancelation-note"
              type="text"
              label="Cancelation Note"
              multiline
              rows={3}
              {...bind}
              fullWidth
            />
            <Typography className={classes.bodyText} color="textSecondary">
              <em>
                * Cancelation Note only required to cancel shipment, not to
                destroy.
              </em>
            </Typography>
            <br />
            <div className={classes.buttonRow}>
              <LoadingButton
                className={classes.button}
                variant="contained"
                color="secondary"
                onClick={handleCancel}
                disabled={value.length === 0}
                loading={isPending}
              >
                CANCEL
              </LoadingButton>
              <LoadingButton
                className={classes.button}
                variant="contained"
                color="secondary"
                onClick={handleDestroy}
                loading={isPending}
              >
                DESTROY
              </LoadingButton>
            </div>
          </div>
          <br />
        </DialogContent>
      </Dialog>
    </div>
  );
};

EditShippingParameterVariantModal.propTypes = {
  open: PropTypes.bool.isRequired,
  shippingParameterVariantId: PropTypes.string.isRequired,
  purchaseOrderId: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default memo(EditShippingParameterVariantModal);
